import React, { forwardRef, useRef, useImperativeHandle, useEffect } from 'react';

export default forwardRef(function GetPaginaOcho(props, ref) {
  const { logoSrc } = props || {};
  const wrapperRef = useRef(null);
  const canvasRef = useRef(null);

  const draw = (opts = {}) => {
    return new Promise((resolve) => {
      const canvas = canvasRef.current;
      const wrapper = wrapperRef.current;
      if (!canvas) return resolve();

      const containerWidth = wrapper ? wrapper.clientWidth : 800;
      const width = Math.max(600, containerWidth);
      const height = Math.round(width * (8.5 / 11));
      const dpr = window.devicePixelRatio || 1;

      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;

      const ctx = canvas.getContext('2d');
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
      ctx.clearRect(0, 0, width, height);

      // bg
      ctx.fillStyle = opts.bgColor || props.bgColor || '#ffffff';
      ctx.fillRect(0, 0, width, height);

      // layout paddings
      const padding = 24;
      let y = padding;

      // title square + title (1.3)
      const titleFontSize = opts.titleFontSize || 45;
      const squareSize = typeof opts.titleSquareSize === 'number' ? opts.titleSquareSize : Math.round(titleFontSize * 1.5);
      const squareGap = typeof opts.titleSquareGap === 'number' ? opts.titleSquareGap : 25;
      const squareText = opts.titleSquareText || '2.1';

      // square
      ctx.fillStyle = opts.titleSquareBg || '#0ea5e9';
      ctx.fillRect(padding, y, squareSize, squareSize);

      ctx.fillStyle = opts.titleSquareColor || '#ffffff';
      ctx.font = `bold ${Math.round(squareSize * 0.55)}px Inter, Arial, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(squareText, padding + squareSize / 2, y + squareSize / 2);

      // title (wrapped/justified up to 700px then wrap)
      const title = opts.title || props.title || 'Lugares de ocurrencia del Desplazamiento Forzado Intraurbano';
      const titleX = padding + squareSize + squareGap;
      const titleMaxW = Math.min(typeof opts.titleMaxWidth === 'number' ? opts.titleMaxWidth : 1100, width - titleX - padding);
      ctx.fillStyle = opts.titleColor || '#111827';
      ctx.font = `bold ${titleFontSize}px Inter, Arial, sans-serif`;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';

      const justifyAndWrap = (c, text, x, y0, maxW, lineH) => {
        const words = String(text).split(/\s+/).filter(Boolean);
        const lines = [];
        let cur = [];
        let curW = 0;
        const spaceW = c.measureText(' ').width;
        for (let w of words) {
          const ww = c.measureText(w).width;
          if (cur.length === 0) {
            cur.push(w); curW = ww;
          } else if (curW + spaceW + ww <= maxW) {
            cur.push(w); curW += spaceW + ww;
          } else {
            lines.push({ words: cur.slice(), wordsWidth: curW });
            cur = [w]; curW = ww;
          }
        }
        if (cur.length) lines.push({ words: cur.slice(), wordsWidth: curW });

        let y = y0;
        for (let i = 0; i < lines.length; i++) {
          const ln = lines[i];
          if (i < lines.length - 1 && ln.words.length > 1) {
            // justify
            const gaps = ln.words.length - 1;
            const extra = (maxW - ln.wordsWidth) / gaps;
            let cx = x;
            for (let j = 0; j < ln.words.length; j++) {
              c.fillText(ln.words[j], cx, y);
              cx += c.measureText(ln.words[j]).width + spaceW + extra;
            }
          } else {
            c.fillText(ln.words.join(' '), x, y);
          }
          y += lineH;
        }
        return y;
      };

      const titleLineH = Math.round(titleFontSize * 1.2);
      const afterTitleY = justifyAndWrap(ctx, title, titleX, y, titleMaxW, titleLineH);

      // subtitle under title
      const subTitle = opts.subTitle || props.subTitle || '';
      if (subTitle) {
        const subFontSize = typeof opts.subTitleFontSize === 'number' ? opts.subTitleFontSize : 30;
        ctx.font = `${subFontSize}px Inter, Arial, sans-serif`;
        ctx.fillStyle = opts.subTitleColor || '#475569';
        const subLineH = Math.round(subFontSize * 1.2);
        const afterSubY = justifyAndWrap(ctx, subTitle, titleX, afterTitleY + 8, titleMaxW, subLineH);
        y = Math.max(y + squareSize, afterSubY) + 24;
      } else {
        y = Math.max(y + squareSize, afterTitleY) + 24;
      }

      // table data (replaces graphics)
      const graficaUnoData = opts.graficaUnoData || props.graficaUnoData || [];
      const tableData = opts.tableData || props.tableData || graficaUnoData || [];
      const tableTopGap = typeof opts.chartsTopGap === 'number' ? opts.chartsTopGap : 60;
      const tableX = padding;
      const tableY = y + tableTopGap;
      const tableW = Math.max(0, width - padding * 2);
      const rowH = Math.round((opts.tableRowHeight || 28));
      const headerH = Math.round(rowH * 1.1);

      const col1R = Math.floor(tableW * 0.25); // Comuna
      const col2R = Math.floor(tableW * 0.45); // Barrio
      const col3R = Math.floor(tableW * 0.15); // Hogares
      const col4R = tableW - col1R - col2R - col3R; // Personas

      const wrapLines = (c, text, maxWidth) => {
        const words = String(text).split(/\s+/).filter(Boolean);
        const lines = [];
        let line = '';
        for (let i = 0; i < words.length; i++) {
          const test = line ? line + ' ' + words[i] : words[i];
          if (c.measureText(test).width > maxWidth && line) {
            lines.push(line);
            line = words[i];
          } else {
            line = test;
          }
        }
        if (line) lines.push(line);
        return lines.length ? lines : [''];
      };

      const drawSmallWrap = (c, text, x, y0, maxW, lh) => {
        const words = String(text).split(' ');
        let line = '';
        let yPos = y0;
        for (let n = 0; n < words.length; n++) {
          const test = line ? line + ' ' + words[n] : words[n];
          if (c.measureText(test).width > maxW && line) {
            c.fillText(line, x, yPos);
            line = words[n];
            yPos += lh;
          } else {
            line = test;
          }
        }
        if (line) c.fillText(line, x, yPos);
        return yPos + lh;
      };

      // textoUno above table
      const textoUno = opts.textoUno || props.textoUno || 'Tabla 3. Distribución por Barrios, Comunas  y corregimientos de ocurrencia del desplazamiento forzado intraurbano, semana actual.';
      ctx.font = `bold ${opts.bottomFontSize || 20}px Inter, Arial, sans-serif`;
      ctx.fillStyle = opts.bottomColor || '#374151';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      const textoUnoMaxW = width - padding * 2;
      const textoUnoY = y + 12;
      drawSmallWrap(ctx, textoUno, padding, textoUnoY, textoUnoMaxW, 18);

      // header
      ctx.save();
      ctx.fillStyle = opts.tableHeaderBg || '#0f172a';
      ctx.fillRect(tableX, tableY, tableW, headerH);
      ctx.fillStyle = opts.tableHeaderColor || '#ffffff';
      ctx.font = `${opts.tableHeaderSize || 20}px Inter, Arial, sans-serif`;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      const hh = tableY + Math.round(headerH / 2);
      ctx.fillText('Comuna', tableX + 8, hh);
      ctx.fillText('Barrio', tableX + col1R + 8, hh);
      ctx.textAlign = 'center';
      const hogaresColCenterX = tableX + col1R + col2R + Math.round(col3R / 2);
      const personasColCenterX = tableX + col1R + col2R + col3R + Math.round(col4R / 2);
      ctx.fillText('Hogares', hogaresColCenterX, hh);
      ctx.fillText('Personas', personasColCenterX, hh);
      ctx.restore();

      let ry = tableY + headerH;
      ctx.font = `${opts.tableRowSize || 18}px Inter, Arial, sans-serif`;
      let totalHogares = 0;
      let totalPersonas = 0;
      for (let i = 0; i < tableData.length; i++) {
        const r = tableData[i] || {};
        const comuna = String(r.comuna || '');
        const barrio = String(r.barrio || '');
        const hogares = String(r.hogares ?? r.totalHogares ?? r.total ?? '');
        const personas = String(r.personas ?? r.totalPersonas ?? '');
        const hogaresNum = parseInt(String(hogares).replace(/\D/g, ''), 10) || 0;
        const personasNum = parseInt(String(personas).replace(/\D/g, ''), 10) || 0;
        totalHogares += hogaresNum;
        totalPersonas += personasNum;

        const comunaLines = wrapLines(ctx, comuna, col1R - 16);
        const barrioLines = wrapLines(ctx, barrio, col2R - 16);
        const numLines = Math.max(1, comunaLines.length, barrioLines.length);
        const blockH = rowH * numLines;

        ctx.fillStyle = opts.tableRowBg || '#ffffff';
        ctx.fillRect(tableX, ry, tableW, blockH);

        ctx.fillStyle = opts.tableRowColor || '#0f172a';
        ctx.textAlign = 'left';
        ctx.textBaseline = 'top';
        const comunaStartY = ry + (blockH - (comunaLines.length * rowH)) / 2 + rowH / 4;
        for (let li = 0; li < comunaLines.length; li++) {
          ctx.fillText(comunaLines[li], tableX + 8, comunaStartY + li * rowH);
        }
        const barrioStartY = ry + (blockH - (barrioLines.length * rowH)) / 2 + rowH / 4;
        for (let li = 0; li < barrioLines.length; li++) {
          ctx.fillText(barrioLines[li], tableX + col1R + 8, barrioStartY + li * rowH);
        }

        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(hogares, hogaresColCenterX, ry + blockH / 2);
        ctx.fillText(personas, personasColCenterX, ry + blockH / 2);

        ctx.strokeStyle = opts.tableBorderColor || 'rgba(15,23,42,0.06)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(tableX, ry + blockH - 1);
        ctx.lineTo(tableX + tableW, ry + blockH - 1);
        ctx.stroke();

        ry += blockH;
      }

      if (tableData.length > 0) {
        const totalRowH = rowH;
        ctx.fillStyle = opts.tableTotalRowBg || '#ffffff';
        ctx.fillRect(tableX, ry, tableW, totalRowH);

        ctx.fillStyle = opts.tableTotalLabelColor || '#0f172a';
        ctx.font = `bold ${opts.tableRowSize || 18}px Inter, Arial, sans-serif`;
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';
        ctx.fillText('Total', tableX + 8, ry + totalRowH / 2);

        ctx.textAlign = 'center';
        ctx.fillText(String(totalHogares), hogaresColCenterX, ry + totalRowH / 2);
        ctx.fillText(String(totalPersonas), personasColCenterX, ry + totalRowH / 2);

        ctx.strokeStyle = opts.tableBorderColor || 'rgba(15,23,42,0.06)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(tableX, ry + totalRowH - 1);
        ctx.lineTo(tableX + tableW, ry + totalRowH - 1);
        ctx.stroke();

        ry += totalRowH;
      }

      // outer table border and vertical separators
      try {
        const tableHeight = Math.max(1, ry - tableY);
        ctx.strokeStyle = opts.tableBorderColor || 'rgba(15,23,42,0.06)';
        ctx.lineWidth = 2;
        ctx.strokeRect(tableX + 1, tableY + 1, tableW - 2, tableHeight - 2);
        const sep1 = Math.round(tableX + col1R);
        const sep2 = Math.round(tableX + col1R + col2R);
        const sep3 = Math.round(tableX + col1R + col2R + col3R);
        ctx.beginPath();
        ctx.moveTo(sep1 + 0.5, tableY);
        ctx.lineTo(sep1 + 0.5, tableY + tableHeight);
        ctx.moveTo(sep2 + 0.5, tableY);
        ctx.lineTo(sep2 + 0.5, tableY + tableHeight);
        ctx.moveTo(sep3 + 0.5, tableY);
        ctx.lineTo(sep3 + 0.5, tableY + tableHeight);
        ctx.stroke();
      } catch (e) { /* ignore */ }

      // footer (muted) at page bottom
      const footerText = opts.footerText || props.footerText || '';
      ctx.fillStyle = opts.footerColor || '#6b7280';
      ctx.font = `${opts.footerFontSize || 26}px Inter, Arial, sans-serif`;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'bottom';
      ctx.fillText(footerText, padding, height - padding - 6);

      // optional logo bottom-right (footer)
      const mainLogoSrc = opts.logoSrc || logoSrc;
      if (mainLogoSrc) {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          try {
            const w = Math.min(200, width * 0.18);
            const h = (img.height / img.width) * w;
            const lx = width - padding - w;
            // place logo at bottom-right corner (footer)
            const ly = height - padding - h;
            ctx.drawImage(img, lx, ly, w, h);
          } catch (e) { /* ignore */ }
          resolve({ canvas, width, height });
        };
        img.onerror = () => resolve({ canvas, width, height });
        img.src = mainLogoSrc;
      } else {
        resolve({ canvas, width, height });
      }
    });
  };

  const download = (filename) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const a = document.createElement('a');
    a.href = canvas.toDataURL('image/png');
    a.download = filename || `pagina_ocho_${new Date().toISOString().slice(0,10)}.png`;
    a.click();
  };

  useImperativeHandle(ref, () => ({
    draw,
    download,
    getDataUrl: () => {
      const canvas = canvasRef.current;
      return canvas ? canvas.toDataURL('image/png') : null;
    }
  }), [draw]);

  useEffect(() => {
    draw();
    const onResize = () => draw();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div ref={wrapperRef} style={{ width: '100%' }}>
      <canvas ref={canvasRef} style={{ width: '100%', display: 'block', borderRadius: 6 }} />
    </div>
  );
});